import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';

import { useGlobalInfo } from '@/utils/hooks/context/useGlobalInfo';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

const MAX_INIT_RETRIES = 3;

export function AdobeExpress() {
    const { user } = useUser();
    const { globalInfo, dispatch } = useGlobalInfo();
    const { lang } = useTranslation();

    const [isLoaded, setIsLoaded] = useState(false);
    const [retryTrigger, setRetryTrigger] = useState(0);
    const initializingRef = useRef(false);
    const prevLangRef = useRef(lang);

    useEffect(() => {
        if (lang !== prevLangRef.current) {
            prevLangRef.current = lang;
            if (retryTrigger !== 0) {
                setRetryTrigger(0);
                return;
            }
        }

        if (!isLoaded || globalInfo?.adobeExpress || initializingRef.current) return;
        if (retryTrigger >= MAX_INIT_RETRIES) return;

        initializingRef.current = true;

        window.CCEverywhere.initialize(
            {
                clientId: process.env.NEXT_PUBLIC_ADOBE_EXPRESS_CLIENT_ID,
                appName: 'Iconosquare'
            },
            { locale: `${lang}_${lang.toUpperCase()}` }
        )
            .then((ccEverywhere) => {
                dispatch({
                    type: 'INIT_ADOBE_EXPRESS',
                    payload: ccEverywhere
                });
            })
            .catch(() => {
                initializingRef.current = false;
                setRetryTrigger((c) => c + 1);
            });
    }, [isLoaded, lang, retryTrigger]);

    if (!user) return null;

    return (
        <Script
            src="https://sdk.cc-embed.adobe.com/v3/CCEverywhere.js"
            onLoad={() => setIsLoaded(true)}
        />
    );
}
